import { useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import { selectIsEditMode } from 'features/common/editModeSlice';
import counterPatternService from 'features/CounterPattern/counterPatternService';
import { UpdateCounterPatternMobileOrderDisplayRequestPayload } from 'features/CounterPattern/models';
import { CounterPattern } from 'features/ImportData/models';
import { COUPON_CLASSIFICATIONS } from 'features/MenuList/constants';
import { FormEvent, useRef } from 'react';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { displayError, handleServiceError } from 'utils';

export interface MobileOrderDisplayModalProps {
  posCodeId: number;
  posCode: string;
  posName: string;
  counterPattern: CounterPattern;
  menuName: string;
  menuCode: string;
  couponClassification: number;
  defaultMobileOrderDisplayFlg: boolean;
  onClose?: (isDataChanged?: boolean) => void;
}

function CounterPatternMobileOrderDisplayModal({
  posCodeId,
  posCode,
  posName,
  counterPattern,
  menuName,
  menuCode,
  couponClassification,
  defaultMobileOrderDisplayFlg,
  onClose,
}: MobileOrderDisplayModalProps) {
  const { t } = useTranslation();
  const { showGlobalModal } = useGlobalModal();
  const formRef = useRef<HTMLFormElement>(null);

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  function handleClose() {
    onClose?.();
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (formRef.current) {
      const chkElement = formRef.current['mobile_order_display_flg'] as HTMLInputElement;
      const mobileOrderDisplayFlg = chkElement.checked;

      // compose payload
      const payload: UpdateCounterPatternMobileOrderDisplayRequestPayload = {
        counterPatternId: counterPattern.id,
        posCodeId: posCodeId,
        mobileOrderDisplayFlg: mobileOrderDisplayFlg,
      };

      (async () => {
        try {
          await counterPatternService.updateMobileOrderDisplay(payload);
          onClose?.(true);
        } catch (e) {
          if (!handleServiceError(showGlobalModal, e as AxiosError)) {
            displayError(showGlobalModal, e as AxiosError);
          }
        }
      })();
    }
  }

  return (
    <Modal show backdrop="static" onHide={handleClose} centered>
      <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
        <FeatureTitleBar title={t('CounterPatternMobileOrderDisplayModal.head_title')}></FeatureTitleBar>
      </Modal.Header>
      <Modal.Body className={`pb-3 ${isEditMode ? 'reedit-mode-wrapper' : ''}`}>
        <form ref={formRef} onSubmit={handleSubmit}>
          <Row className="mb-4">
            <Col>
              <div>
                {t('CounterPatternMobileOrderDisplayModal.counter_pattern')}：&nbsp;{counterPattern.name}&nbsp;(
                {counterPattern.cd})
              </div>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="mb-3">{t('CounterPatternMobileOrderDisplayModal.menu_information')}</div>
              <div className="mb-3 ps-3">
                {t('CounterPatternMobileOrderDisplayModal.menu')}：&nbsp;&nbsp;{menuName}&nbsp;&nbsp;(
                {menuCode + (COUPON_CLASSIFICATIONS[couponClassification] || '')})
              </div>
            </Col>
          </Row>
          <Row className="mb-4">
            <Col sm={9}>
              <strong className="d-inline-block ps-3 fw-bold">
                POS：&nbsp;&nbsp;{posName}&nbsp;&nbsp;({posCode})
              </strong>
            </Col>
            <Col sm={3}>
              <Form.Check
                type="switch"
                label=""
                name="mobile_order_display_flg"
                style={{ transform: 'scale(1.75)' }}
                defaultChecked={defaultMobileOrderDisplayFlg}
              />
            </Col>
          </Row>
          <Row>
            <Col className="text-end">
              <Button type="submit" className="btn--dark-green">
                {t('CounterPatternMobileOrderDisplayModal.save')}
              </Button>
            </Col>
          </Row>
        </form>
      </Modal.Body>
    </Modal>
  );
}

export default CounterPatternMobileOrderDisplayModal;
